import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusChip } from './StatusChip';

export type ActionKind = 'primary' | 'navy' | 'danger' | 'svc' | 'us' | 'default';
export interface Action {
  label: string;
  kind?: ActionKind;
  onClick?: () => void;
  disabled?: boolean;
  /** why the button is disabled (native tooltip) — a status machine step or a document the server cannot render yet */
  title?: string;
  /** action name recorded on the signature when the action is PIN-signed (opens SignaturePinModal first) */
  pin?: string;
  /**
   * On a list, an action with no `onClick` of its own that exports the rows on screen — `ListPage` wires it to the same
   * control its footer offers. The screen inventory puts an Export button in the header of some lists; this is how one
   * gets there without every screen building the export itself.
   */
  exportFormat?: 'xlsx' | 'pdf';
}

// Mirrors mockup pageHeader(): title · doc no · status chip · subtitle · actions (right). Action order = inventory order.
//
// A disabled action with nothing to say reads as a system that does not work. Most of them are disabled because the
// document is in a status that does not allow them, or because this role does not hold the permission — spec §4.2
// separates duties, so an administrator may configure the section and not raise a demand in it. Where a screen has not
// given its own reason, the button at least says that it is unavailable rather than sitting there dead.
export function PageHeader({ title, docNo, status, subtitle, actions = [] }: { title: string; docNo?: string | null; status?: string | null; subtitle?: ReactNode; actions?: Action[] }) {
  const { t } = useTranslation();
  return (
    <div className="ph">
      <div className="t">
        <h1>
          {title}
          {docNo && <span className="docno">{docNo}</span>} {status && <StatusChip status={status} />}
        </h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      <div className="act">
        {actions.map((a) => (
          <button
            key={a.label}
            type="button"
            className={`btn${a.kind && a.kind !== 'default' ? ` ${a.kind}` : ''}`}
            title={a.title ?? (a.disabled ? t('action.unavailable') : undefined)}
            onClick={a.onClick}
            disabled={a.disabled}
          >
            {a.label}
          </button>
        ))}
      </div>
    </div>
  );
}
